import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { CrudType } from "@core/enums/crud-type";
import { ServiceHelper } from "@core/helpers/service-helper";
import { BandwidthAssetRisk } from "@core/models/bandwidth-asset-risk.model";
import { BandwidthFundRisk } from "@core/models/bandwidth-fund-risk.model";
import { Composition } from "@core/models/composition.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { CompositionsInfo } from "@core/models/compositions-info.model";
import { FundComposition } from "@core/models/fund-composition.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { environment } from "@env/environment";
import { map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CompositionModelService {
  constructor(private http: HttpClient) {}

  getCompositionModelList$(
    searchTerm?: string
  ): Observable<Array<Composition>> {
    const params = ServiceHelper.getListQueryParams(searchTerm);

    return this.http
      .get<
        Array<Composition>
      >(`${environment.BASE_URL}composition/`, { params })
      .pipe(
        map((response: Array<Composition>): Array<Composition> => {
          return response;
        })
      );
  }

  getCompositionModelListInfo$(): Observable<CompositionsInfo> {
    return this.http
      .get<CompositionsInfo>(`${environment.BASE_URL}composition/info`)
      .pipe(
        map((listInformation: CompositionsInfo): CompositionsInfo => {
          return listInformation;
        })
      );
  }

  getProductFundList$(id: string): Observable<Array<FundWithRelated>> {
    const params = new HttpParams().set("id", id);
    return this.http
      .get<
        Array<FundWithRelated>
      >(`${environment.BASE_URL}fund/prodfund`, { params })
      .pipe(
        map((response: Array<FundWithRelated>): Array<FundWithRelated> => {
          return response;
        })
      );
  }

  getRiskProfilesList$(): Observable<Array<RiskProfile>> {
    return this.http
      .get<Array<RiskProfile>>(`${environment.BASE_URL}riskprofile`)
      .pipe(
        map((response: Array<RiskProfile>): Array<RiskProfile> => {
          return response;
        })
      );
  }

  saveCompositionModel$(
    compositionModel: Partial<Composition>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    const composition = {
      ...compositionModel,
      riskProfileId: compositionModel.riskProfile?.id,
    };
    delete composition["id"];
    delete composition["riskProfile"];
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}composition/${id}`,
      composition
    );
  }

  saveCompositionModelVersion$(
    compositionModelVersion: Partial<CompositionVersion>,
    versionId: string | undefined
  ): Observable<string> {
    const version = {
      ...compositionModelVersion,
    };
    delete version["id"];
    delete version["fundCompositions"];
    delete version["bandwidthAssetRisks"];
    delete version["bandwidthFundRisks"];
    // riskProfileId is a model-level field, not a version field. It leaks into
    // the form value as an empty string and, when POSTed, the empty string
    // fails to bind to the back-end's Guid, producing a 400 with no response
    // body. Strip it so the version payload only carries version fields.
    delete (version as { riskProfileId?: string })["riskProfileId"];
    if (versionId === "") {
      return this.http.post<string>(
        `${environment.BASE_URL}compositionversion/`,
        version
      );
    }
    return this.http.put<string>(
      `${environment.BASE_URL}compositionversion/${versionId}`,
      version
    );
  }

  cancelCompositionModelVersion$(versionId: string): Observable<unknown> {
    return this.http.patch(
      `${environment.BASE_URL}compositionversion/${versionId}/cancel`,
      {}
    );
  }

  saveFundCompositionList$(
    fundCompositions: Array<FundComposition>,
    id: string | undefined
  ): Observable<unknown> {
    return this.http[id ? CrudType.Put : CrudType.Post](
      `${environment.BASE_URL}fund/composition`,
      fundCompositions
    );
  }

  saveBandwidthAssetRiskList$(
    bandwidthAssetRisks: Array<BandwidthAssetRisk>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}bandwidth`,
      bandwidthAssetRisks
    );
  }

  saveBandwidthFundRiskList$(
    bandwidthFundRisks: Array<BandwidthFundRisk>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}bandwidthfund`,
      bandwidthFundRisks
    );
  }

  getCompositionModel$(id: string): Observable<Composition> {
    // TODO update map after api returns object not array with one object
    return this.http
      .get<Array<Composition>>(`${environment.BASE_URL}composition/${id}`)
      .pipe(map((response: Array<Composition>): Composition => response[0]));
  }
}
